/* eslint-disable prefer-const */
import { Pipe, PipeTransform } from '@angular/core';
import { Params } from '@angular/router';
import {
  Opportunity as ApiOpportunity,
  DurationInterface,
  ForecastingInterface,
  LineItem,
} from '@vendasta/sales-opportunities';

export interface OrdersUrl {
  route: string;
  queryParams?: Params;
}

export const ACCOUNT_INFO_ROOT = 'info';
export const ACCOUNT_INFO = (businessId: string) => `/${ACCOUNT_INFO_ROOT}/${businessId}`;

export enum ClosedLostReason {
  Price,
  NoBudget,
  LostToCompetitor,
  NotReady,
  Other,
}

export type OpportunityUrlFunction = (opportunity: Opportunity) => string;

export type AccountUrlFunction = (opportunity: Opportunity | ApiOpportunity) => string;

export type CompanyUrlFunction = (companyId: string) => string;

export type OrdersUrlFunction = (opportunity: ApiOpportunity) => OrdersUrl;

export const ClosedLostReasonStringMapping = {
  price: ClosedLostReason.Price,
  'no-budget': ClosedLostReason.NoBudget,
  'lost-to-competitor': ClosedLostReason.LostToCompetitor,
  'not-ready': ClosedLostReason.NotReady,
  other: ClosedLostReason.Other,
};

export interface DetailsConfig {
  showCRMDetails: boolean;
  getAccountDetailsUrl?: AccountUrlFunction;
  getCompanyDetailsUrl?: CompanyUrlFunction;
}

export class Opportunity {
  public opportunityId: string;
  public accountGroupId: string;
  public salesPersonId: string;
  public name: string;
  public projectedFirstYearValue: number;
  public forecasting: ForecastingInterface;
  public preferredCurrency: string;

  public expectedCloseDate: Date;
  public actualClosedDate: Date;
  public probability: number;
  public notes: string;
  public isClosed: boolean;
  public closedLostReason: ClosedLostReason;
  public pipelineStage: string;

  public created: Date;
  public updated: Date;
  public lastConnectedDate: Date;
  public lastActivityDate: Date;

  public reopenedReason: string;
  public tags: string[];
  public opportunityDetailsUrl: string;
  public expectedContractDuration: DurationInterface;

  public attachments: Attachment[];

  public canReopen: boolean;
  public pipelineId: string;

  public lineItems: LineItem[];
  public subscriberUserIds: string[];

  constructor({
    opportunityId,
    accountGroupId,
    salesPersonId,
    name,
    projectedFirstYearValue,
    forecasting,
    expectedCloseDate,
    actualClosedDate,
    probability,
    isClosed,
    closedLostReason,
    pipelineStage,
    notes,
    created,
    updated,
    lastConnectedDate,
    lastActivityDate,
    reopenedReason,
    tags,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    opportunityDetailsUrl,
    preferredCurrency,
    attachments,
    expectedContractDuration,
    canReopen,
    pipelineId,
    lineItems,
    subscriberUserIds,
  }: Opportunity) {
    this.opportunityId = opportunityId;
    this.accountGroupId = accountGroupId;
    this.salesPersonId = salesPersonId;
    this.name = name;
    this.projectedFirstYearValue = projectedFirstYearValue;
    this.forecasting = forecasting;
    this.preferredCurrency = preferredCurrency;
    this.expectedCloseDate = expectedCloseDate;
    this.actualClosedDate = actualClosedDate;
    this.probability = probability;
    this.isClosed = isClosed;
    this.closedLostReason = closedLostReason;
    this.pipelineStage = pipelineStage;
    this.notes = notes;
    this.created = created;
    this.updated = updated;
    this.lastConnectedDate = lastConnectedDate;
    this.lastActivityDate = lastActivityDate;
    this.reopenedReason = reopenedReason;
    this.tags = tags;
    this.opportunityDetailsUrl = `/info/${accountGroupId}/opportunity/${opportunityId}/`;
    this.attachments = attachments;
    this.expectedContractDuration = expectedContractDuration;
    this.canReopen = canReopen;
    this.pipelineId = pipelineId;
    this.lineItems = lineItems;
    this.subscriberUserIds = subscriberUserIds;
  }

  public static createOpportunity(rawOpportunity: any, preferredCurrency: string): Opportunity {
    if (!rawOpportunity) {
      return null;
    }
    let opportunityId,
      accountGroupId,
      salesPersonId,
      name,
      projectedFirstYearValue,
      forecasting,
      expectedCloseDate,
      actualClosedDate,
      probability,
      isClosed,
      closedLostReason,
      pipelineStage,
      notes,
      created,
      updated,
      lastConnectedDate,
      reopenedReason,
      tags,
      opportunityDetailsUrl,
      attachments,
      expectedContractDuration,
      canReopen,
      pipelineId;

    opportunityId = rawOpportunity.opportunity_id;
    accountGroupId = rawOpportunity.account_group_id;
    salesPersonId = rawOpportunity.sales_person_id;
    name = rawOpportunity.name;
    projectedFirstYearValue = rawOpportunity.projected_first_year_value;
    forecasting = this.convertSTForecastingRevenueFromRaw(rawOpportunity.forecasting || {});
    expectedCloseDate = rawOpportunity.expected_close_date ? new Date(rawOpportunity.expected_close_date) : null;
    actualClosedDate = rawOpportunity.actual_closed_date ? new Date(rawOpportunity.actual_closed_date) : null;
    probability = rawOpportunity.probability > 0 ? rawOpportunity.probability : null;
    isClosed = rawOpportunity.is_closed;
    closedLostReason = rawOpportunity.closed_lost_reason;
    pipelineStage = rawOpportunity.pipeline_stage;

    notes = rawOpportunity.notes;
    created = new Date(rawOpportunity.created);
    updated = new Date(rawOpportunity.updated);
    lastConnectedDate = rawOpportunity.last_connected_date ? new Date(rawOpportunity.last_connected_date) : null;
    const lastActivityDate = rawOpportunity.last_activity_date ? new Date(rawOpportunity.last_activity_date) : null;

    reopenedReason = rawOpportunity.reopened_reason;
    tags = rawOpportunity.tags;
    opportunityDetailsUrl = rawOpportunity.opportunityDetailsUrl;
    attachments = []; // attachments only come from the microservice
    expectedContractDuration = rawOpportunity.expectedContractDuration;
    canReopen = rawOpportunity.canReopen;
    pipelineId = rawOpportunity.pipeline_id;
    const lineItems = rawOpportunity.line_items;
    const subscriberUserIds = rawOpportunity.subscriberUserIds;

    return new Opportunity({
      opportunityId,
      accountGroupId,
      salesPersonId,
      name,
      projectedFirstYearValue,
      forecasting,
      isClosed,
      closedLostReason,
      pipelineStage,
      expectedCloseDate,
      actualClosedDate,
      probability,
      notes,
      created,
      updated,
      lastConnectedDate,
      lastActivityDate,
      reopenedReason,
      tags,
      opportunityDetailsUrl,
      preferredCurrency,
      attachments,
      expectedContractDuration,
      canReopen,
      pipelineId,
      lineItems,
      subscriberUserIds,
    });
  }

  public static createOpportunityFromMicroservice(
    rawOpportunity: ApiOpportunity,
    preferredCurrency: string,
  ): Opportunity {
    if (!rawOpportunity) {
      return null;
    }
    let opportunityId,
      accountGroupId,
      salesPersonId,
      name,
      projectedFirstYearValue,
      forecasting,
      expectedCloseDate,
      actualClosedDate,
      probability,
      isClosed,
      closedLostReason,
      pipelineStage,
      notes,
      created,
      updated,
      lastConnectedDate,
      reopenedReason,
      tags,
      opportunityDetailsUrl,
      attachments: Attachment[],
      expectedContractDuration: DurationInterface,
      canReopen,
      pipelineId;

    opportunityId = rawOpportunity.opportunityId;
    accountGroupId = rawOpportunity.accountGroupId;
    salesPersonId = rawOpportunity.salesPersonId;
    name = rawOpportunity.name;
    projectedFirstYearValue = rawOpportunity.projectedFirstYearValue || 0;
    forecasting =
      rawOpportunity.forecasting ||
      <ForecastingInterface>{ revenueInMonth: { recurringRevenue: 0, initialRevenue: 0 } };
    expectedCloseDate =
      rawOpportunity.expectedCloseDate.getFullYear() > 1970 ? new Date(rawOpportunity.expectedCloseDate) : null;
    actualClosedDate =
      rawOpportunity.actualClosedDate.getFullYear() > 1970 ? new Date(rawOpportunity.actualClosedDate) : null;
    probability = rawOpportunity.probability > 0 ? rawOpportunity.probability : null;
    isClosed = rawOpportunity.isClosed;
    closedLostReason = rawOpportunity.closedLostReason;
    pipelineStage = rawOpportunity.pipelineStage;

    notes = rawOpportunity.notes;
    created = new Date(rawOpportunity.created);
    updated = new Date(rawOpportunity.updated);
    lastConnectedDate =
      rawOpportunity.lastConnectedDate.getFullYear() > 1970 ? new Date(rawOpportunity.lastConnectedDate) : null;
    const lastActivityDate =
      rawOpportunity.lastActivityDate.getFullYear() > 1970 ? new Date(rawOpportunity.lastActivityDate) : null;

    reopenedReason = rawOpportunity.reopenedReason;
    tags = rawOpportunity.tags || [];
    opportunityDetailsUrl = ''; // The opportunityDetailsUrl isn't used
    attachments = rawOpportunity.attachments;
    expectedContractDuration = rawOpportunity.expectedContractDuration;
    canReopen = rawOpportunity.canReopen;
    pipelineId = rawOpportunity.pipelineId;
    const lineItems = rawOpportunity.lineItems;
    const subscriberUserIds = rawOpportunity.subscriberUserIds;

    return new Opportunity({
      opportunityId,
      accountGroupId,
      salesPersonId,
      name,
      projectedFirstYearValue,
      forecasting,
      isClosed,
      closedLostReason,
      pipelineStage,
      expectedCloseDate,
      actualClosedDate,
      probability,
      notes,
      created,
      updated,
      lastConnectedDate,
      lastActivityDate,
      reopenedReason,
      tags,
      opportunityDetailsUrl,
      preferredCurrency,
      attachments,
      expectedContractDuration,
      canReopen,
      pipelineId,
      lineItems,
      subscriberUserIds,
    });
  }

  public static createNewOpportunity(rawOpportunity: any): Opportunity {
    if (!rawOpportunity) {
      return null;
    }
    const coercedValues = {
      projectedFirstYearValue: rawOpportunity.projectedFirstYearValue ? rawOpportunity.projectedFirstYearValue : 0,
      expectedCloseDate: rawOpportunity.expectedCloseDate ? new Date(rawOpportunity.expectedCloseDate) : null,
      actualClosedDate: rawOpportunity.actualClosedDate ? new Date(rawOpportunity.actualClosedDate) : null,
      probability: rawOpportunity.probability > 0 ? rawOpportunity.probability : null,
      created: new Date(rawOpportunity.created),
      updated: new Date(rawOpportunity.updated),
      lastConnectedDate: rawOpportunity.lastConnectedDate ? new Date(rawOpportunity.lastConnectedDate) : null,
      lastActivityDate: rawOpportunity.lastActivityDate ? new Date(rawOpportunity.lastActivityDate) : null,
    };

    const opportunity = Object.assign(rawOpportunity, coercedValues);

    return new Opportunity(opportunity);
  }

  static convertSTForecastingRevenueFromRaw(rawForecastingRevenue: any): ForecastingInterface {
    const forecast = <ForecastingInterface>{ revenueInMonth: {} };
    forecast.revenueInMonth.initialRevenue = rawForecastingRevenue?.revenue_in_month?.initial_revenue || 0;
    forecast.revenueInMonth.recurringRevenue = rawForecastingRevenue?.revenue_in_month?.recurring_revenue || 0;
    return forecast;
  }
}

export interface Attachment {
  attachmentId: string;
  name: string;
  url: string;
  created: Date;
}

@Pipe({
  name: 'getOpportunityUrl',
})
export class GetOpportunityUrl implements PipeTransform {
  transform(accountGroupId: string, opportunityId: string): string {
    return `${ACCOUNT_INFO(accountGroupId)}/opportunity/${opportunityId}`;
  }
}
